import classNames from 'classnames';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AuthApi } from '../api';
import { AuthRoute, LoadingOverlay, Select } from '../components';
import { RoutePath } from '../config';
import { useAuthContext } from '../hooks';
import { DoctorAuthLayout } from '../layouts';

interface CredentialsFields {
  password: string;
  username: string;
}

interface HospitalFields {
  hospital_id?: number;
}

export const DoctorLoginPage = () => {
  const { signIn } = useAuthContext();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [loginError, setLoginError] = useState<string | null>(null);

  const credentialsForm = useForm<CredentialsFields>({
    defaultValues: {
      username: 'doctor_demo',
    },
  });

  const hospitalForm = useForm<HospitalFields>();

  const { mutateAsync: loginDoctor, isLoading: isLoggingIn } = useMutation({
    mutationFn: (data: CredentialsFields) => AuthApi.authorizeDoctor(data),
    onSuccess: () => {
      setLoginError(null);
      setStep(2);
    },
    onError: (err: any) => {
      setLoginError(
        err?.response?.data?.message ?? 'Неверный логин или пароль',
      );
    },
  });

  const { data: hospitals = [], isLoading: isLoadingHospitals } = useQuery({
    queryKey: ['doctor', 'hospitals'],
    queryFn: () => AuthApi.getDoctorHospitals(),
    enabled: step === 2,
  });

  const { mutateAsync: selectHospital, isLoading: isSelectingHospital } =
    useMutation({
      mutationFn: (hospital_id: string) =>
        AuthApi.selectDoctorHospital(hospital_id),
      onSuccess: async () => {
        const check = await AuthApi.checkAuth();
        const status = (check as any)?.status;
        const data = (check as any)?.data;
        const userId = data?.user ?? data?.id;
        if (status === 'success' && userId != null) {
          signIn(userId, false);
          navigate(RoutePath.DoctorCreateOrder);
        }
      },
    });

  const onCredentialsSubmit: SubmitHandler<CredentialsFields> = useCallback(
    async (data) => {
      setLoginError(null);
      await loginDoctor(data);
    },
    [loginDoctor],
  );

  const onHospitalSubmit: SubmitHandler<HospitalFields> = useCallback(
    async (data) => {
      if (data.hospital_id == null) return;
      await selectHospital(String(data.hospital_id));
    },
    [selectHospital],
  );

  const handleBackToCredentials = useCallback(() => {
    setStep(1);
    setLoginError(null);
    hospitalForm.reset();
  }, [hospitalForm]);

  return (
    <AuthRoute redirectPath={RoutePath.DoctorCreateOrder}>
      <DoctorAuthLayout>
        {(isLoggingIn || isSelectingHospital) && <LoadingOverlay />}
        <div className="mx-auto w-full max-w-[360px]">
          <h1 className="mb-[36px] text-center text-[30px] font-medium leading-[36px]">
            Личный кабинет
          </h1>

          {step === 1 && (
            <form
              onSubmit={credentialsForm.handleSubmit(onCredentialsSubmit)}
              className="grid gap-[12px]"
            >
              <input
                {...credentialsForm.register('username', { required: true })}
                className={classNames(
                  'input',
                  credentialsForm.formState.errors.username && 'input--invalid',
                )}
                placeholder="Имя пользователя"
                type="text"
              />
              <input
                {...credentialsForm.register('password', { required: true })}
                className={classNames(
                  'input',
                  credentialsForm.formState.errors.password && 'input--invalid',
                )}
                placeholder="Пароль"
                type="password"
              />
              {loginError && (
                <div className="text-red-500 text-sm">{loginError}</div>
              )}
              <button
                className="button button--block button--primary"
                type="submit"
                disabled={isLoggingIn}
              >
                Войти
              </button>
            </form>
          )}

          {step === 2 && (
            <form
              onSubmit={hospitalForm.handleSubmit(onHospitalSubmit)}
              className="grid gap-[12px]"
            >
              <p className="text-brand-1/80">
                Выберите клинику для входа в личный кабинет
              </p>
              {isLoadingHospitals ? (
                <div className="py-[40px] text-center text-brand-1/60">
                  Загрузка списка клиник...
                </div>
              ) : hospitals.length === 0 ? (
                <div className="py-[40px] text-center text-brand-1/60">
                  Нет доступных клиник
                </div>
              ) : (
                <Controller
                  control={hospitalForm.control}
                  name="hospital_id"
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <Select
                      error={fieldState.error}
                      onChange={field.onChange}
                      options={hospitals.map((h) => ({
                        label: h.name,
                        value: h.id,
                      }))}
                      placeholder="Выбор клиники"
                      value={field.value}
                    />
                  )}
                />
              )}
              <div className="flex gap-[20px]">
                <button
                  type="button"
                  className="button button--block button--outlined"
                  onClick={handleBackToCredentials}
                >
                  Назад
                </button>
                <button
                  className="button button--block button--primary"
                  type="submit"
                  disabled={
                    isLoadingHospitals ||
                    isSelectingHospital ||
                    hospitals.length === 0 ||
                    hospitalForm.watch('hospital_id') == null
                  }
                >
                  Войти в кабинет
                </button>
              </div>
            </form>
          )}
        </div>
      </DoctorAuthLayout>
    </AuthRoute>
  );
};
